import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { devisService } from '@/services/devis'
import type { DevisFormData, DevisFilters, DevisStatus, SignatureData } from '@/types/devis'

export function useDevis(filters?: DevisFilters) {
  const queryClient = useQueryClient()

  const devisQuery = useQuery({
    queryKey: ['devis', filters],
    queryFn: () => devisService.getAll(filters),
  })

  const createMutation = useMutation({
    mutationFn: (data: DevisFormData) => devisService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devis'] })
      queryClient.invalidateQueries({ queryKey: ['devis-stats'] })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<DevisFormData> }) =>
      devisService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devis'] })
    },
  })

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: DevisStatus }) =>
      devisService.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devis'] })
      queryClient.invalidateQueries({ queryKey: ['devis-stats'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => devisService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devis'] })
      queryClient.invalidateQueries({ queryKey: ['devis-stats'] })
    },
  })

  const sendMutation = useMutation({
    mutationFn: (id: string) => devisService.sendDevis(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devis'] })
      queryClient.invalidateQueries({ queryKey: ['devis-stats'] })
    },
  })

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => devisService.duplicate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devis'] })
      queryClient.invalidateQueries({ queryKey: ['devis-stats'] })
    },
  })

  const convertMutation = useMutation({
    mutationFn: (id: string) => devisService.convertToFacture(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devis'] })
      queryClient.invalidateQueries({ queryKey: ['devis-stats'] })
    },
  })

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      devisService.rejectDevis(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devis'] })
      queryClient.invalidateQueries({ queryKey: ['devis-stats'] })
      queryClient.invalidateQueries({ queryKey: ['interventions'] })
    },
  })

  return {
    devisList: devisQuery.data ?? [],
    isLoading: devisQuery.isLoading,
    error: devisQuery.error,
    refetch: devisQuery.refetch,
    createDevis: createMutation.mutateAsync,
    updateDevis: updateMutation.mutateAsync,
    updateDevisStatus: updateStatusMutation.mutateAsync,
    deleteDevis: deleteMutation.mutateAsync,
    sendDevis: sendMutation.mutateAsync,
    duplicateDevis: duplicateMutation.mutateAsync,
    convertToFacture: convertMutation.mutateAsync,
    rejectDevisWithReason: rejectMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isSending: sendMutation.isPending,
    isDuplicating: duplicateMutation.isPending,
    isConverting: convertMutation.isPending,
    isRejecting: rejectMutation.isPending,
  }
}

export function useDevisById(id: string) {
  return useQuery({
    queryKey: ['devis', id],
    queryFn: () => devisService.getById(id),
    enabled: !!id,
  })
}

export function useDevisStats() {
  return useQuery({
    queryKey: ['devis-stats'],
    queryFn: () => devisService.getStats(),
  })
}

export function useDevisNumero() {
  return useQuery({
    queryKey: ['devis-numero'],
    queryFn: () => devisService.generateNumero(),
  })
}

// Hook pour la page publique (client)
export function usePublicDevis(token: string) {
  const queryClient = useQueryClient()

  const devisQuery = useQuery({
    queryKey: ['public-devis', token],
    queryFn: async () => {
      const devis = await devisService.getByToken(token)
      if (devis) {
        // Tracker la vue (ne bloque pas le chargement)
        devisService.trackView(token).catch(() => {
          // Silently ignore tracking errors
        })
      }
      return devis
    },
    enabled: !!token,
  })

  const signMutation = useMutation({
    mutationFn: (signatureData: SignatureData) =>
      devisService.signDevis(token, signatureData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['public-devis', token] })
    },
  })

  const rejectMutation = useMutation({
    mutationFn: (message?: string) =>
      devisService.rejectDevisByToken(token, message),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['public-devis', token] })
    },
  })

  return {
    devis: devisQuery.data,
    isLoading: devisQuery.isLoading,
    error: devisQuery.error,
    signDevis: signMutation.mutateAsync,
    rejectDevis: rejectMutation.mutateAsync,
    isSigning: signMutation.isPending,
    isRejecting: rejectMutation.isPending,
  }
}
